import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { base44 } from '@/api/base44Client';
import { CheckCircle2 } from 'lucide-react';

/**
 * How often this person hears from the hub.
 *
 * Everyone starts on the daily summary. The alternative is offered rather than
 * hidden because a Construction Manager waiting on a plan review genuinely may
 * want each one as it lands - but it is the second option, not the default,
 * because the default is what almost everyone should be on.
 *
 * There is no "off". Someone who stops all their notifications and then misses
 * a deadline has no way of knowing why, and neither does anyone else.
 */
const options = [
  {
    value: 'daily',
    label: 'One summary a day',
    detail:
      'A single email each weekday morning with everything from the last 24 hours, grouped by project.',
  },
  {
    value: 'immediate',
    label: 'Every update as it happens',
    detail:
      'A separate email for each task, milestone, plan review and deadline reminder. This can be a lot of email.',
  },
];

export default function EmailPreferencesDialog({ open, onClose }) {
  const queryClient = useQueryClient();
  const { data: currentUser } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me(),
    staleTime: 60_000,
  });

  const [mode, setMode] = useState('daily');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  /* Seed from the server's answer once it arrives, and again each time the
     dialog is reopened, so cancelling out of a change does not leave the old
     selection showing next time. */
  useEffect(() => {
    if (open && currentUser?.notification_mode) setMode(currentUser.notification_mode);
  }, [open, currentUser?.notification_mode]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await base44.auth.setNotificationMode(mode);
      /* base44.auth.me() caches for the page's lifetime and React Query keeps
         its own copy, so both have to be told before the new value shows. */
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => setSaved(false), 300);
  };

  const unchanged = mode === (currentUser?.notification_mode || 'daily');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Email Preferences</DialogTitle>
          <DialogDescription>
            Choose how often the Project Hub emails you. This affects your own email only.
          </DialogDescription>
        </DialogHeader>

        {saved ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
            <p className="font-semibold text-foreground">Saved</p>
            <p className="text-sm text-muted-foreground">
              {mode === 'daily'
                ? 'You will get one summary each morning.'
                : 'You will get each update as it happens.'}
            </p>
            <Button onClick={handleClose} className="mt-2">Close</Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <RadioGroup value={mode} onValueChange={setMode} className="space-y-3">
              {options.map(option => (
                <label
                  key={option.value}
                  htmlFor={`email-pref-${option.value}`}
                  className={`flex gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    mode === option.value
                      ? 'border-accent bg-accent/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <RadioGroupItem
                    value={option.value}
                    id={`email-pref-${option.value}`}
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <Label
                      htmlFor={`email-pref-${option.value}`}
                      className="font-medium cursor-pointer"
                    >
                      {option.label}
                    </Label>
                    <p className="text-xs text-muted-foreground">{option.detail}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving || unchanged}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
